/**
 * Project Name: Brainsie eCommerce v4
 * Component: Simple Card
 * Description: Simple card component with heading, subheading, and descriptive text
 * Author: Brainsie Team
 * Date Created: October 29, 2025
 */

import React from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";

export default function SimpleCard({
  component,
  section,
  darkMode,
  theme,
  gridIndex,
  settings = {},
  renderListContent,
  renderCTAButtons,
}) {
  // Get component data using correct field mapping
  const heading = component.Primary_Text || "";
  const subHeading = component.Secondary_Text || "";
  const descriptiveText = component.Descriptive_Text || "";
  const listContent = component.List_Content || "";
  const primaryCTA = component.Primary_CTA || "";
  const secondaryCTA = component.Secondary_CTA || "";

  // Enhanced component settings with comprehensive defaults
  const componentSettings = {
    size: "medium",
    variant: "contained",
    color: "primary",
    fullWidth: false,
    disabled: false,
    fontSize: "",
    fontWeight: "",
    textAlign: "center",
    backgroundColor: "",
    textColor: "",
    borderRadius: "",
    padding: "",
    margin: "",
    elevation: 2,
    customStyles: "",
    showBackground: true,
    showShadow: true,
    showBorder: false,
    showGridNumber: true,
    ...settings,
    ...(component.Component_Settings
      ? typeof component.Component_Settings === "string"
        ? JSON.parse(component.Component_Settings)
        : component.Component_Settings
      : {}),
  };

  // Enhanced styling functions
  const getTextColor = () =>
    componentSettings.textColor ||
    (darkMode ? theme.palette.common.white : theme.palette.text.primary);

  const getCardBackground = () =>
    componentSettings.backgroundColor ||
    (darkMode ? theme.palette.grey[800] : theme.palette.background.paper);

  const getCardStyles = () => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor:
      componentSettings.showBackground !== false
        ? getCardBackground()
        : "transparent",
    borderRadius: componentSettings.borderRadius || "8px",
    border: componentSettings.showBorder
      ? `1px solid ${theme.palette.divider}`
      : "none",
    boxShadow:
      componentSettings.elevation > 0
        ? `0px ${componentSettings.elevation}px ${
            componentSettings.elevation * 2
          }px rgba(0,0,0,0.12)`
        : componentSettings.showShadow !== false
        ? "0 4px 12px rgba(0,0,0,0.1)"
        : "none",
    transition: "all 0.3s ease",
    opacity: componentSettings.disabled ? 0.6 : 1,
    "&:hover": {
      transform: componentSettings.disabled ? "none" : "translateY(-4px)",
      boxShadow: componentSettings.disabled
        ? "inherit"
        : "0 8px 25px rgba(0,0,0,0.15)",
    },
    ...(componentSettings.customStyles
      ? (() => {
          try {
            return typeof componentSettings.customStyles === "string"
              ? JSON.parse(componentSettings.customStyles)
              : componentSettings.customStyles;
          } catch {
            return {};
          }
        })()
      : {}),
  });

  const getHeadingSize = () => {
    if (componentSettings.fontSize) return componentSettings.fontSize;
    return componentSettings.size === "large"
      ? "1.75rem"
      : componentSettings.size === "small"
      ? "1.1rem"
      : "1.5rem";
  };

  const getBodySize = () => {
    if (componentSettings.fontSize) return componentSettings.fontSize;
    return componentSettings.size === "large"
      ? "1.1rem"
      : componentSettings.size === "small"
      ? "0.875rem"
      : "1rem";
  };

  // Don't render if no heading content
  if (!heading || heading === "NA") {
    return null;
  }

  console.log("SimpleCard rendering:", {
    heading,
    subHeading,
    descriptiveText,
  });

  return (
    <Card sx={getCardStyles()}>
      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          textAlign: componentSettings.textAlign || "center",
          p: componentSettings.padding || 3,
          height: "100%",
        }}
      >
        {/* Grid Number (01, 02, 03) */}
        {componentSettings.showGridNumber !== false && gridIndex && (
          <Typography
            variant="h4"
            sx={{
              color: componentSettings.textColor || theme.palette.text.disabled,
              fontWeight: 300,
              fontSize:
                componentSettings.size === "large"
                  ? "3rem"
                  : componentSettings.size === "small"
                  ? "1.8rem"
                  : "2.5rem",
              mb: 2,
              opacity: 0.6,
              textAlign: componentSettings.textAlign || "center",
            }}
          >
            {gridIndex.toString().padStart(2, "0")}
          </Typography>
        )}

        {/* Media Files (Top of card if exists) */}
        {component.Media_Files && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {(() => {
              try {
                let mediaFiles;
                if (typeof component.Media_Files === "string") {
                  mediaFiles = JSON.parse(component.Media_Files);
                } else {
                  mediaFiles = component.Media_Files;
                }

                const mediaArray = Array.isArray(mediaFiles)
                  ? mediaFiles
                  : Object.values(mediaFiles);
                const mediaFile = mediaArray[0];

                if (mediaFile) {
                  const imageUrl = mediaFile.startsWith("http")
                    ? mediaFile
                    : `${process.env.REACT_APP_BASEURL || ""}${mediaFile}`;

                  return (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={heading}
                      sx={{
                        width:
                          componentSettings.size === "large"
                            ? 80
                            : componentSettings.size === "small"
                            ? 40
                            : 60,
                        height:
                          componentSettings.size === "large"
                            ? 80
                            : componentSettings.size === "small"
                            ? 40
                            : 60,
                        objectFit: "contain",
                        borderRadius: componentSettings.borderRadius || "4px",
                      }}
                    />
                  );
                }
              } catch (e) {
                return null;
              }
            })()}
          </Box>
        )}

        {/* Primary Text (Main Heading) */}
        <Typography
          variant="h5"
          component="h3"
          sx={{
            mb: subHeading && subHeading !== "NA" ? 1.5 : 2,
            color: getTextColor(),
            fontWeight: componentSettings.fontWeight || 600,
            fontSize: getHeadingSize(),
            lineHeight: 1.3,
            textAlign: componentSettings.textAlign || "center",
          }}
        >
          {heading}
        </Typography>

        {/* Secondary Text (Sub Heading) */}
        {subHeading && subHeading !== "NA" && (
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              color: getTextColor(),
              fontWeight: componentSettings.fontWeight || 500,
              fontSize:
                componentSettings.size === "large"
                  ? "1.1rem"
                  : componentSettings.size === "small"
                  ? "0.9rem"
                  : "1rem",
              opacity: 0.8,
              textAlign: componentSettings.textAlign || "center",
            }}
          >
            {subHeading}
          </Typography>
        )}

        {/* Descriptive Text */}
        {descriptiveText && descriptiveText !== "NA" && (
          <Typography
            variant="body2"
            sx={{
              mb: listContent || primaryCTA || secondaryCTA ? 2 : 0,
              color: getTextColor(),
              lineHeight: 1.6,
              fontSize: getBodySize(),
              opacity: 0.85,
              flexGrow: !(listContent || primaryCTA || secondaryCTA) ? 1 : 0,
              textAlign: componentSettings.textAlign || "center",
            }}
          >
            {descriptiveText}
          </Typography>
        )}

        {/* List Content (JSON Array) */}
        {listContent && renderListContent && (
          <Box
            sx={{ mb: primaryCTA || secondaryCTA ? 2 : 0, textAlign: "left" }}
          >
            {renderListContent(listContent)}
          </Box>
        )}

        {/* CTA Buttons */}
        {(primaryCTA || secondaryCTA) && renderCTAButtons && (
          <Box sx={{ mt: "auto", pt: 2 }}>
            {renderCTAButtons(primaryCTA, secondaryCTA)}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
